import React, { useEffect, useMemo, useState } from "react";
import {
    Search,
    RefreshCw,
    Mail,
    Phone,
    MoreHorizontal,
    CalendarDays,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_WEBSITE_LEADS_API_URL;

/* =========================================================
   PAT TOKEN
========================================================= */

const getPatToken = () => {
    return (
        import.meta.env?.VITE_PAT_TOKEN ||
        process.env.REACT_APP_PAT_TOKEN ||
        process.env.VITE_PAT_TOKEN ||
        ""
    );
};

/* =========================================================
   HELPERS
========================================================= */

const getLeadName = (lead, type) => {
    if (type === "calls") {
        const fullName = `${lead.firstname || ""} ${lead.lastname || ""
            }`.trim();

        return fullName || "-";
    }

    const fullName = `${lead.firstName || ""} ${lead.lastName || ""
        }`.trim();

    return (
        fullName ||
        lead.name ||
        lead.fullName ||
        "-"
    );
};

const getLeadEmail = (lead) => {
    return lead.email || "-";
};

const getLeadPhone = (lead) => {
    return lead.phone || "-";
};

const getLeadMessage = (lead) => {
    return (
        lead.description ||
        lead.message ||
        "-"
    );
};

const formatScheduledTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return parsedDate.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getReceivedDate = (lead) => {
    if (!lead.createdAt) return "-";

    const createdDate = new Date(lead.createdAt);

    if (Number.isNaN(createdDate.getTime())) {
        return "-";
    }

    const diff = Date.now() - createdDate.getTime();

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
        return `${Math.max(minutes, 1)}m ago`;
    }

    if (hours < 24) {
        return `${hours}h ago`;
    }

    if (days < 30) {
        return `${days}d ago`;
    }

    return createdDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatStatus = (status) => {
    if (!status) return "New";

    return (
        status.charAt(0).toUpperCase() +
        status.slice(1)
    );
};

/* =========================================================
   COMPONENT
========================================================= */

const Base2brandLeads = () => {
    const [activeTab, setActiveTab] = useState("enquiries");

    const [leads, setLeads] = useState([]);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(1);

    const [limit] = useState(20);

    const [hasNextPage, setHasNextPage] = useState(false);

    /* =========================================================
       FETCH DATA
    ========================================================= */

    const fetchLeads = async () => {
        const PAT_TOKEN = getPatToken();

        if (!PAT_TOKEN) {
            setError(
                "PAT token missing. Add VITE_PAT_TOKEN in your frontend .env file and restart the frontend."
            );

            setLeads([]);

            return;
        }

        try {
            setLoading(true);

            setError("");

            const endpoint =
                activeTab === "enquiries"
                    ? "/main-enquiries"
                    : "/schedule-calls";

            const response = await fetch(
                `${API_BASE_URL}${endpoint}?page=${page}&limit=${limit}`,
                {
                    method: "GET",

                    headers: {
                        Authorization: `Bearer ${PAT_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();

            console.log(
                `${activeTab} API Response:`,
                data
            );

            if (!response.ok) {
                throw new Error(
                    data?.message ||
                    `HTTP Error: ${response.status}`
                );
            }

            /* =====================================================
               MAIN ENQUIRIES RESPONSE
      
               {
                 enquiries: [...]
               }
      
               SCHEDULE CALL RESPONSE
      
               {
                 scheduleCalls: [...]
               }
      
               Also support data/results if backend changes.
            ===================================================== */

            let records = [];

            if (activeTab === "enquiries") {
                records =
                    data?.enquiries ||
                    data?.data ||
                    data?.results ||
                    [];
            } else {
                records =
                    data?.scheduleCalls ||
                    data?.schedule_calls ||
                    data?.calls ||
                    data?.data ||
                    data?.results ||
                    [];
            }

            if (!Array.isArray(records)) {
                records = [];
            }

            setLeads(records);

            setHasNextPage(
                Boolean(
                    data?.hasNextPage ??
                    data?.hasNext ??
                    data?.pagination?.hasNextPage ??
                    records.length === limit
                )
            );
        } catch (err) {
            console.error(
                "Error fetching leads:",
                err
            );

            setError(
                err?.message ||
                "Failed to fetch leads."
            );

            setLeads([]);

            setHasNextPage(false);
        } finally {
            setLoading(false);
        }
    };

    /* =========================================================
       TAB CHANGE
    ========================================================= */

    useEffect(() => {
        setPage(1);

        setSearch("");
    }, [activeTab]);

    /* =========================================================
       FETCH WHEN PAGE / TAB CHANGES
    ========================================================= */

    useEffect(() => {
        fetchLeads();
    }, [page, activeTab]);

    /* =========================================================
       SEARCH
    ========================================================= */

    const filteredLeads = useMemo(() => {
        if (!search.trim()) {
            return leads;
        }

        const query = search
            .trim()
            .toLowerCase();

        return leads.filter((lead) => {
            const name = getLeadName(
                lead,
                activeTab
            ).toLowerCase();

            const email = getLeadEmail(
                lead
            ).toLowerCase();

            const phone = getLeadPhone(
                lead
            ).toLowerCase();

            const message = getLeadMessage(
                lead
            ).toLowerCase();

            return (
                name.includes(query) ||
                email.includes(query) ||
                phone.includes(query) ||
                message.includes(query)
            );
        });
    }, [leads, search, activeTab]);

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <div className="min-h-screen bg-[#080808] px-5 py-8 text-white md:px-8 lg:px-10">
            <div className="mx-auto max-w-[1520px]">

                {/* =====================================================
            HEADER
        ===================================================== */}

                <div className="mb-8">

                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[3px] text-[#9b5cff]">
                        Content Management
                    </p>

                    <h1 className="text-[38px] font-medium leading-none tracking-[-1.5px] text-white md:text-[42px]">
                        Leads
                    </h1>

                    <p className="mt-3 text-[14px] text-[#777]">
                        Enquiries submitted through the website.
                    </p>

                </div>

                {/* =====================================================
            TABS
        ===================================================== */}

                <div className="mb-2 inline-flex rounded-[11px] bg-[#222] p-[3px]">

                    <button
                        onClick={() =>
                            setActiveTab("enquiries")
                        }
                        className={`rounded-[9px] px-4 py-[7px] text-[14px] font-medium transition-all ${activeTab === "enquiries"
                            ? "bg-[#090909] text-white shadow-[0_1px_5px_rgba(0,0,0,.5)]"
                            : "text-[#888] hover:text-white"
                            }`}
                    >
                        Contact Enquiries
                    </button>

                    <button
                        onClick={() =>
                            setActiveTab("calls")
                        }
                        className={`rounded-[9px] px-4 py-[7px] text-[14px] font-medium transition-all ${activeTab === "calls"
                            ? "bg-[#090909] text-white shadow-[0_1px_5px_rgba(0,0,0,.5)]"
                            : "text-[#888] hover:text-white"
                            }`}
                    >
                        Schedule a Call
                    </button>

                </div>

                {/* =====================================================
            SEARCH + REFRESH
        ===================================================== */}

                <div className="mb-4 flex items-center justify-between gap-4">

                    <div className="relative w-full max-w-[512px]">

                        <Search
                            size={17}
                            strokeWidth={1.6}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#777]"
                        />

                        <input
                            type="text"
                            value={search}
                            onChange={(e) =>
                                setSearch(e.target.value)
                            }
                            placeholder={
                                activeTab === "enquiries"
                                    ? "Search leads by name, email, phone..."
                                    : "Search call requests by name, email, phone..."
                            }
                            className="h-[40px] w-full rounded-[10px] border border-[#292929] bg-[#0b0b0b] pl-10 pr-4 text-[14px] text-white outline-none placeholder:text-[#777] transition focus:border-[#444]"
                        />

                    </div>

                    <button
                        onClick={fetchLeads}
                        disabled={loading}
                        title="Refresh"
                        className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[11px] border border-[#303030] bg-[#111] text-[#aaa] transition hover:border-[#444] hover:bg-[#181818] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >

                        <RefreshCw
                            size={15}
                            className={
                                loading
                                    ? "animate-spin"
                                    : ""
                            }
                        />

                    </button>

                </div>

                {/* =====================================================
            ERROR
        ===================================================== */}

                {error && (
                    <div className="mb-4 rounded-[10px] border border-red-900/50 bg-red-950/20 px-4 py-3 text-sm text-red-400">
                        {error}
                    </div>
                )}

                {/* =====================================================
            TABLE
        ===================================================== */}

                <div className="overflow-hidden rounded-[12px] border border-[#292929] bg-[#111]">

                    <div className="overflow-x-auto">

                        <table className="w-full min-w-[1000px] border-collapse">

                            {/* =================================================
                  TABLE HEADER
              ================================================= */}

                            <thead>

                                <tr className="border-b border-[#242424] bg-[#0c0c0c]">

                                    <th className="w-[25%] px-4 py-[13px] text-left text-[10px] font-semibold uppercase tracking-[2px] text-[#686868]">
                                        Name
                                    </th>

                                    <th className="w-[25%] px-4 py-[13px] text-left text-[10px] font-semibold uppercase tracking-[2px] text-[#686868]">
                                        Contact
                                    </th>

                                    <th className="w-[32%] px-4 py-[13px] text-left text-[10px] font-semibold uppercase tracking-[2px] text-[#686868]">

                                        {activeTab === "enquiries"
                                            ? "Message"
                                            : "Scheduled Time"}

                                    </th>

                                    <th className="w-[9%] px-4 py-[13px] text-left text-[10px] font-semibold uppercase tracking-[2px] text-[#686868]">
                                        Status
                                    </th>

                                    <th className="w-[8%] px-4 py-[13px] text-left text-[10px] font-semibold uppercase tracking-[2px] text-[#686868]">
                                        Received
                                    </th>

                                    <th className="w-[35px] px-3 py-[13px]" />

                                </tr>

                            </thead>

                            {/* =================================================
                  TABLE BODY
              ================================================= */}

                            <tbody>

                                {/* =================================================
                    LOADING
                ================================================= */}

                                {loading ? (
                                    Array.from({
                                        length: 8,
                                    }).map((_, index) => (

                                        <tr
                                            key={index}
                                            className="border-b border-[#202020]"
                                        >

                                            <td className="px-4 py-5">

                                                <div className="h-4 w-[130px] animate-pulse rounded bg-[#222]" />

                                                <div className="mt-2 h-3 w-[100px] animate-pulse rounded bg-[#1c1c1c]" />

                                            </td>

                                            <td className="px-4 py-5">

                                                <div className="h-3 w-[170px] animate-pulse rounded bg-[#222]" />

                                                <div className="mt-2 h-3 w-[120px] animate-pulse rounded bg-[#1c1c1c]" />

                                            </td>

                                            <td className="px-4 py-5">

                                                <div className="h-3 w-[80%] animate-pulse rounded bg-[#222]" />

                                            </td>

                                            <td className="px-4 py-5">

                                                <div className="h-7 w-[55px] animate-pulse rounded-full bg-[#222]" />

                                            </td>

                                            <td className="px-4 py-5">

                                                <div className="h-3 w-[50px] animate-pulse rounded bg-[#222]" />

                                            </td>

                                            <td />

                                        </tr>

                                    ))
                                ) : filteredLeads.length > 0 ? (

                                    /* =================================================
                                       DATA
                                    ================================================= */

                                    filteredLeads.map(
                                        (lead, index) => {

                                            const isCall =
                                                activeTab ===
                                                "calls";

                                            const name =
                                                getLeadName(
                                                    lead,
                                                    activeTab
                                                );

                                            const email =
                                                getLeadEmail(
                                                    lead
                                                );

                                            const phone =
                                                getLeadPhone(
                                                    lead
                                                );

                                            const message =
                                                getLeadMessage(
                                                    lead
                                                );

                                            const status =
                                                lead.status ||
                                                (isCall
                                                    ? "pending"
                                                    : "new");

                                            return (

                                                <tr
                                                    key={
                                                        lead._id ||
                                                        lead.id ||
                                                        index
                                                    }
                                                    className="group border-b border-[#202020] transition hover:bg-[#161616]"
                                                >

                                                    {/* =====================================
                              NAME
                          ===================================== */}

                                                    <td className="px-4 py-[15px] align-middle">

                                                        <div className="max-w-[240px]">

                                                            <p className="truncate text-[14px] font-semibold text-[#e8e8e8]">
                                                                {name}
                                                            </p>

                                                            <p className="mt-[3px] truncate text-[11px] text-[#666]">

                                                                {isCall
                                                                    ? "Schedule a Call"
                                                                    : lead.requirement ||
                                                                    "Website Enquiry"}

                                                            </p>

                                                        </div>

                                                    </td>

                                                    {/* =====================================
                              CONTACT
                          ===================================== */}

                                                    <td className="px-4 py-[15px] align-middle">

                                                        <div className="space-y-[4px]">

                                                            <div className="flex items-center gap-2">

                                                                <Mail
                                                                    size={13}
                                                                    strokeWidth={
                                                                        1.5
                                                                    }
                                                                    className="shrink-0 text-[#666]"
                                                                />

                                                                <span className="max-w-[230px] truncate text-[12px] text-[#c1c1c1]">
                                                                    {email}
                                                                </span>

                                                            </div>

                                                            <div className="flex items-center gap-2">

                                                                <Phone
                                                                    size={13}
                                                                    strokeWidth={
                                                                        1.5
                                                                    }
                                                                    className="shrink-0 text-[#666]"
                                                                />

                                                                <span className="text-[12px] text-[#aaa]">
                                                                    {phone}
                                                                </span>

                                                            </div>

                                                        </div>

                                                    </td>

                                                    {/* =====================================
                              MESSAGE / SCHEDULED TIME
                          ===================================== */}

                                                    <td className="px-4 py-[15px] align-middle">

                                                        {isCall ? (

                                                            <div className="flex items-start gap-2">

                                                                <CalendarDays
                                                                    size={14}
                                                                    strokeWidth={
                                                                        1.5
                                                                    }
                                                                    className="mt-[1px] shrink-0 text-[#666]"
                                                                />

                                                                <div>

                                                                    <p className="text-[12px] text-[#c1c1c1]">
                                                                        {formatScheduledTime(
                                                                            lead.scheduledTime
                                                                        )}
                                                                    </p>

                                                                    <p className="mt-1 text-[10px] text-[#666]">
                                                                        Scheduled time
                                                                    </p>

                                                                </div>

                                                            </div>

                                                        ) : (

                                                            <p
                                                                title={
                                                                    message
                                                                }
                                                                className="max-w-[560px] truncate text-[12px] text-[#999]"
                                                            >
                                                                {message}
                                                            </p>

                                                        )}

                                                    </td>

                                                    {/* =====================================
                              STATUS
                          ===================================== */}

                                                    <td className="px-4 py-[15px] align-middle">

                                                        <span
                                                            className={`inline-flex rounded-full border px-3 py-[5px] text-[11px] font-medium ${isCall
                                                                ? "border-[#343434] bg-[#1b1b1b] text-[#bdbdbd]"
                                                                : "border-[#542c73] bg-[#21152c] text-[#c58cff]"
                                                                }`}
                                                        >
                                                            {formatStatus(
                                                                status
                                                            )}
                                                        </span>

                                                    </td>

                                                    {/* =====================================
                              RECEIVED
                          ===================================== */}

                                                    <td className="whitespace-nowrap px-4 py-[15px] align-middle text-[11px] text-[#666]">

                                                        {getReceivedDate(
                                                            lead
                                                        )}

                                                    </td>

                                                    {/* =====================================
                              ACTION
                          ===================================== */}

                                                    <td className="px-3 py-[15px] text-right">

                                                        <button
                                                            type="button"
                                                            className="rounded-md p-1 text-[#666] transition hover:bg-[#242424] hover:text-white"
                                                            title="More options"
                                                        >
                                                            <MoreHorizontal
                                                                size={16}
                                                            />
                                                        </button>

                                                    </td>

                                                </tr>

                                            );
                                        }
                                    )

                                ) : (

                                    /* =================================================
                                       EMPTY STATE
                                    ================================================= */

                                    <tr>

                                        <td
                                            colSpan="6"
                                            className="px-6 py-20 text-center"
                                        >

                                            <div className="mx-auto max-w-[300px]">

                                                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-[#292929] bg-[#151515]">

                                                    <Search
                                                        size={17}
                                                        className="text-[#666]"
                                                    />

                                                </div>

                                                <p className="text-[14px] font-medium text-[#ddd]">
                                                    No leads found
                                                </p>

                                                <p className="mt-1 text-[12px] text-[#666]">

                                                    {search
                                                        ? "Try changing your search."
                                                        : activeTab ===
                                                            "calls"
                                                            ? "There are no scheduled calls available."
                                                            : "There are no enquiries available."}

                                                </p>

                                            </div>

                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

                {/* =====================================================
            PAGINATION
        ===================================================== */}

                <div className="flex items-center justify-between py-5">

                    <button
                        onClick={() =>
                            setPage((prev) =>
                                Math.max(prev - 1, 1)
                            )
                        }
                        disabled={
                            page === 1 ||
                            loading
                        }
                        className="rounded-[8px] border border-[#292929] bg-[#111] px-4 py-2 text-[12px] text-[#aaa] transition hover:bg-[#191919] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                    >
                        ← Previous
                    </button>

                    <span className="text-[11px] text-[#666]">
                        Page {page}
                    </span>

                    <button
                        onClick={() =>
                            setPage(
                                (prev) => prev + 1
                            )
                        }
                        disabled={
                            !hasNextPage ||
                            loading
                        }
                        className="rounded-[8px] border border-[#292929] bg-[#111] px-4 py-2 text-[12px] text-[#aaa] transition hover:bg-[#191919] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                    >
                        Next →
                    </button>

                </div>

            </div>

        </div>
    );
};

export default Base2brandLeads;